import { sealData, unsealData } from "iron-session";
import { isError } from "lodash";

import { OakGoogleClassroomException } from "@/errors";
import { EncryptionService } from "@/services/Encryption";
import { FirestoreClient } from "@/services/FirestoreClient";
import { UserCredentialsEntity } from "@/types/services";

export type EncryptedSession = {
  loginHint: string;
  accessToken: string;
};

export type SessionAndUser = {
  session: EncryptedSession;
  user: UserCredentialsEntity;
};

export type EncryptedCredentials = {
  encryptedRefreshToken?: string;
  encryptedProfileUrl?: string;
};

export class OakSessions {
  constructor(
    private readonly sessionSecret: string,
    private readonly encryptionService: EncryptionService,
    private readonly firestoreClient: FirestoreClient,
  ) {}

  /**
   * Creates an encrypted session containing the user's login hint and access token.
   * @param loginHint - The user's Google account id
   * @param accessToken - The user's current access token
   */
  public async sealSession(
    loginHint: string,
    accessToken: string,
  ): Promise<string> {
    try {
      return await sealData({ loginHint, accessToken } as EncryptedSession, {
        password: this.sessionSecret,
      });
    } catch (error) {
      throw OakGoogleClassroomException.OakGoogleClassroomError(
        "Failed to seal session",
        "sealSession",
        { loginHint },
        isError(error) ? error : undefined,
      );
    }
  }

  /**
   * Decrypts an encrypted session string.
   * Returns null if the session is invalid or missing required fields.
   * @param session - The encrypted session string to decrypt
   */
  public async decryptSession(
    session: string,
  ): Promise<EncryptedSession | null> {
    const decryptedSession = await unsealData<EncryptedSession>(session, {
      password: this.sessionSecret,
    });
    if (!decryptedSession?.accessToken || !decryptedSession?.loginHint)
      return null;
    return decryptedSession;
  }

  /**
   * Validates an encrypted session and fetches the associated user credentials.
   * Automatically decrypts the user's refresh token and profile picture URL.
   * Returns null for an invalid session.
   */
  public async getSessionAndUser(
    encryptedSession: string,
  ): Promise<SessionAndUser | null> {
    const session = await this.decryptSession(encryptedSession);
    if (!session) return null;

    const user = await this.firestoreClient.getConnectedUserCredentials(
      session.loginHint,
    );
    if (!user) return null;

    const decryptedRefreshToken = user.refreshToken
      ? this.encryptionService.decryptString(user.refreshToken)
      : undefined;
    user.refreshToken = decryptedRefreshToken;

    if (process.env.GOOGLE_CLASSROOM_DEV_REFRESH_TOKEN) {
      user.refreshToken = process.env.GOOGLE_CLASSROOM_DEV_REFRESH_TOKEN;
    }

    if (user.profilePictureUrl) {
      const decryptedProfilePictureUrl = this.encryptionService.decryptString(
        user.profilePictureUrl,
      );
      user.profilePictureUrl = decryptedProfilePictureUrl;
    }

    return { session, user };
  }

  /**
   * Encrypts a user's refresh token and profile picture URL
   * @param refreshToken - Optional refresh token to encrypt
   * @param profilePictureUrl - Optional profile picture URL to encrypt
   */
  public encryptCredentials(
    refreshToken?: string,
    profilePictureUrl?: string,
  ): EncryptedCredentials {
    const encryptedRefreshToken = refreshToken
      ? this.encryptionService.encryptString(refreshToken)
      : undefined;
    const encryptedProfileUrl = profilePictureUrl
      ? this.encryptionService.encryptString(profilePictureUrl)
      : undefined;
    return { encryptedRefreshToken, encryptedProfileUrl };
  }

  /**
   * Saves user credentials to Firestore.
   * @param credentials - The user credentials to save (should already be encrypted)
   */
  public async saveUserCredentials(credentials: {
    loginHint: string;
    refreshToken?: string;
    profilePictureUrl?: string;
  }): Promise<void> {
    await this.firestoreClient.upsertConnectedUserCredentials(credentials);
  }
}
